import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { 
  MessageSquare, Sparkles, Zap, Store, 
  Video, Bot, CreditCard, Palette, Rocket, CheckCircle,
  ArrowRight, Loader, ShieldCheck, Smartphone, BarChart3,
  ChevronRight, Star, Users, Globe
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HERO_VIDEO_URL = 'https://videos.pexels.com/video-files/3571264/3571264-hd_1920_1080_30fps.mp4';
const HERO_VIDEO_SD = 'https://videos.pexels.com/video-files/3571264/3571264-sd_640_360_30fps.mp4';
const HERO_FALLBACK = 'https://static.prod-images.emergentagent.com/jobs/ca1df940-9122-4670-a082-1f1bb680eec2/images/17a9b6eb55b5e694b9c580f31161fd10092c241263bdd557118002b063b2f1da.png';

const DEMO_IMAGES = {
  step1: 'https://static.prod-images.emergentagent.com/jobs/ca1df940-9122-4670-a082-1f1bb680eec2/images/734379f5a2cb91e759ff0bbbcd37a5e35571eee62e9846c515b11f0eafcdfe1b.png',
  step2: 'https://static.prod-images.emergentagent.com/jobs/ca1df940-9122-4670-a082-1f1bb680eec2/images/4dada8ae7feb7a3d538579db6a63ea79211e91063f814b3060bf85241073b014.png',
  step3: 'https://static.prod-images.emergentagent.com/jobs/ca1df940-9122-4670-a082-1f1bb680eec2/images/c7766a13e87b6fc2fafb07d3f435f8c78c94892c59ce2e61fe284a2d7d12c3a9.png',
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' } })
};

// ─── Hero Section ────────────────────────────────────────────
const HeroSection = ({ navigate }) => {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const timer = setTimeout(() => {
      video.src = window.innerWidth > 768 ? HERO_VIDEO_URL : HERO_VIDEO_SD;
      video.load();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        onLoadedData={() => setVideoLoaded(true)}
        poster={HERO_FALLBACK}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ zIndex: 0 }}
      />
      {/* Fallback image while video loads */}
      {!videoLoaded && (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_FALLBACK})`, zIndex: 0 }}
        />
      )}
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/65" style={{ zIndex: 1 }} />
      {/* Gradient overlay at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#09090B] to-transparent" style={{ zIndex: 1 }} />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-8 text-sm font-medium text-emerald-300" data-testid="hero-badge">
            <Zap className="w-4 h-4" />
            Automação com IA para vendedores
          </span>
        </motion.div>

        <motion.h1
          initial="hidden" animate="visible" variants={fadeUp} custom={0.1}
          className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] mb-6 text-white"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="hero-title"
        >
          Transforme qualquer produto em{' '}
          <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
            vendas automáticas
          </span>{' '}
          com IA
        </motion.h1>

        <motion.p
          initial="hidden" animate="visible" variants={fadeUp} custom={0.2}
          className="text-base sm:text-lg lg:text-xl text-zinc-300 max-w-3xl mx-auto mb-10 leading-relaxed"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
          data-testid="hero-subtitle"
        >
          Crie sua loja, automatize o WhatsApp e produza conteúdo que vende — tudo em um só lugar.
        </motion.p>

        <motion.div
          initial="hidden" animate="visible" variants={fadeUp} custom={0.3}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            onClick={() => navigate('/register')}
            className="h-14 px-8 text-base sm:text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:-translate-y-0.5 transition-all"
            data-testid="hero-cta-primary"
          >
            <Rocket className="w-5 h-5 mr-2" />
            Criar minha loja agora
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/register')}
            className="h-14 px-8 text-base sm:text-lg bg-white/10 hover:bg-white/20 text-white rounded-full border border-white/20 backdrop-blur-sm"
            data-testid="hero-cta-secondary"
          >
            Começar grátis
          </Button>
        </motion.div>

        {/* Social proof */}
        <motion.div
          initial="hidden" animate="visible" variants={fadeUp} custom={0.5}
          className="mt-12 flex items-center justify-center gap-6 text-zinc-400 text-sm"
        >
          <span className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-emerald-400" />
            +500 vendedores
          </span>
          <span className="hidden sm:inline text-zinc-600">|</span>
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-blue-400" />
            Pagamentos seguros
          </span>
          <span className="hidden sm:inline text-zinc-600">|</span>
          <span className="flex items-center gap-1.5">
            <Globe className="w-4 h-4 text-cyan-400" />
            100% brasileiro
          </span>
        </motion.div>
      </div>
    </section>
  );
};

// ─── Benefits Grid ────────────────────────────────────────────
const benefits = [
  {
    icon: Bot,
    title: 'WhatsApp com IA',
    desc: 'Assistente inteligente que responde clientes, tira dúvidas e fecha vendas 24h por você.',
    span: 'md:col-span-4 lg:col-span-6',
    accent: 'from-green-500/20 to-emerald-500/20',
    iconBg: 'bg-green-500',
    highlight: true,
  },
  {
    icon: Video,
    title: 'Criador de Conteúdo IA',
    desc: 'Face Swap e Lip Sync para criar vídeos profissionais sem aparecer na câmera.',
    span: 'md:col-span-4 lg:col-span-6',
    accent: 'from-purple-500/20 to-pink-500/20',
    iconBg: 'bg-purple-500',
    highlight: true,
  },
  {
    icon: Palette,
    title: 'Vitrine Personalizável',
    desc: 'Customize cores, banner e layout da sua loja com proteção inteligente de contraste.',
    span: 'md:col-span-4 lg:col-span-4',
    accent: 'from-rose-500/20 to-orange-500/20',
    iconBg: 'bg-rose-500',
  },
  {
    icon: CreditCard,
    title: 'Pagamentos Integrados',
    desc: 'Mercado Pago nativo com PIX, cartão e boleto. Checkout direto na loja.',
    span: 'md:col-span-4 lg:col-span-4',
    accent: 'from-cyan-500/20 to-blue-500/20',
    iconBg: 'bg-cyan-500',
  },
  {
    icon: BarChart3,
    title: 'Gestão Financeira',
    desc: 'Agenda de pagamentos, lembretes e controle de receitas e despesas.',
    span: 'md:col-span-4 lg:col-span-4',
    accent: 'from-amber-500/20 to-yellow-500/20',
    iconBg: 'bg-amber-500',
  },
];

const BenefitsSection = () => (
  <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 relative" data-testid="benefits-section">
    <div className="max-w-7xl mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 block" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Por que escolher a Needi
        </span>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Tudo que você precisa para{' '}
          <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">vender mais</span>
        </h2>
        <p className="text-base sm:text-lg text-zinc-400 mt-4 max-w-2xl mx-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Ferramentas profissionais de automação e IA para escalar seu negócio digital
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5">
        {benefits.map((b, i) => (
          <motion.div
            key={b.title}
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.08}
            className={`${b.span} group`}
          >
            <div className={`h-full bg-zinc-900/60 backdrop-blur-sm border rounded-2xl p-6 transition-all duration-300 ${
              b.highlight ? 'border-white/10 hover:border-blue-500/40 hover:shadow-[0_0_30px_rgba(37,99,235,0.12)]' : 'border-white/5 hover:border-white/15'
            }`}>
              <div className={`w-12 h-12 ${b.iconBg} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                <b.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>{b.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Quick Demo Flow ──────────────────────────────────────────
const steps = [
  { num: '01', title: 'Crie seu produto', desc: 'Adicione fotos, descrição e preço. Nossa IA valida e melhora seu anúncio automaticamente.', img: DEMO_IMAGES.step1 },
  { num: '02', title: 'Compartilhe sua loja', desc: 'Envie seu ID exclusivo por WhatsApp. A IA responde seus clientes e vende por você.', img: DEMO_IMAGES.step2 },
  { num: '03', title: 'Receba e venda', desc: 'Pagamento direto via Mercado Pago. PIX, cartão ou boleto — tudo automático.', img: DEMO_IMAGES.step3 },
];

const DemoSection = () => (
  <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-testid="demo-section">
    {/* Background accent */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/8 rounded-full blur-3xl pointer-events-none" />

    <div className="max-w-7xl mx-auto relative">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 block" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Como funciona
        </span>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Simples assim.{' '}
          <span className="text-zinc-500">3 passos.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {steps.map((s, i) => (
          <motion.div
            key={s.num}
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.15}
            className="group"
          >
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl overflow-hidden hover:border-blue-500/20 transition-all duration-300">
              <div className="aspect-[4/3] bg-zinc-800/50 overflow-hidden relative">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  Passo {s.num}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>{s.desc}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Differentials ────────────────────────────────────────────
const differentials = [
  { icon: Smartphone, title: 'Tudo em um único app', desc: 'Loja, vendas, WhatsApp IA, conteúdo e financeiro — sem precisar de dezenas de ferramentas.' },
  { icon: Zap, title: 'Sem necessidade técnica', desc: 'Interface intuitiva pensada para quem está começando. Publique em minutos.' },
  { icon: ShieldCheck, title: 'Seguro e confiável', desc: 'Pagamentos via Mercado Pago. Dados protegidos. Plataforma 100% brasileira.' },
  { icon: Star, title: 'IA que vende por você', desc: 'Nosso assistente de IA responde clientes automaticamente no WhatsApp, 24 horas.' },
];

const DifferentialsSection = () => (
  <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8" data-testid="differentials-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left Text */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 block" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Diferenciais
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Feito para vendedores{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              iniciantes e avançados
            </span>
          </h2>
          <p className="text-base text-zinc-400 mb-8 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            A Needi reúne tudo o que você precisa para criar, divulgar e vender seus produtos digitais e físicos, com automação inteligente.
          </p>
          <Button
            onClick={() => window.location.href = '/register'}
            className="h-12 px-6 bg-blue-600 hover:bg-blue-500 text-white rounded-full hover:-translate-y-0.5 transition-all"
            data-testid="differentials-cta"
          >
            Comece agora <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </motion.div>

        {/* Right Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {differentials.map((d, i) => (
            <motion.div
              key={d.title}
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.1}
              className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5 hover:border-white/15 transition-colors duration-300"
            >
              <div className="w-10 h-10 bg-zinc-800 rounded-xl flex items-center justify-center mb-3">
                <d.icon className="w-5 h-5 text-blue-400" />
              </div>
              <h4 className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{d.title}</h4>
              <p className="text-xs text-zinc-500 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>{d.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─── Pricing Section ──────────────────────────────────────────
const pricingPlans = [
  {
    name: 'Premium',
    price: 'R$ 39,99',
    credits: '120 créditos/mês',
    features: ['Loja digital privada', 'Até 50 produtos', 'WhatsApp IA básico', 'Mercado Pago integrado'],
    key: 'premium',
  },
  {
    name: 'Pro',
    price: 'R$ 59,99',
    credits: '220 créditos/mês',
    features: ['Tudo do Premium', 'Controle financeiro', 'Páginas de venda IA', 'Agenda de pagamentos', 'Criador de conteúdo'],
    key: 'pro',
    popular: true,
  },
  {
    name: 'Advanced',
    price: 'R$ 79,99',
    credits: '350 créditos/mês',
    features: ['Tudo do Pro', 'Cursos avançados', 'Prioridade no suporte', 'Mais créditos de IA'],
    key: 'advanced',
  },
];

const PricingSection = ({ navigate }) => (
  <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8" data-testid="pricing-section">
    <div className="max-w-5xl mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 block" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Planos
        </span>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Escolha seu plano
        </h2>
        <p className="text-base text-zinc-400 mt-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Sem taxas ocultas. Cancele quando quiser.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {pricingPlans.map((plan, i) => (
          <motion.div
            key={plan.key}
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.12}
            className="relative group"
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full z-10">
                Mais popular
              </div>
            )}
            <div className={`h-full rounded-2xl p-6 transition-all duration-300 ${
              plan.popular
                ? 'bg-zinc-900 border-2 border-blue-500/50 shadow-[0_0_30px_rgba(37,99,235,0.15)]'
                : 'bg-zinc-900/50 border border-white/5 hover:border-white/15'
            }`}>
              <h3 className="text-lg font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</h3>
              <p className="text-xs text-zinc-500 mb-4">{plan.credits}</p>
              <div className="mb-6">
                <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.price}</span>
                <span className="text-sm text-zinc-500">/mês</span>
              </div>
              <ul className="space-y-3 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-300" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate('/register')}
                className={`w-full h-11 rounded-full transition-all ${
                  plan.popular
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)]'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
                data-testid={`pricing-cta-${plan.key}`}
              >
                Começar agora
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      <p className="text-center text-xs text-zinc-600 mt-8" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        Precisa de mais? Temos planos Scale, Business, Elite e Exclusive.{' '}
        <button onClick={() => navigate('/register')} className="text-blue-400 hover:underline">Ver todos os planos</button>
      </p>
    </div>
  </section>
);

// ─── Final CTA ────────────────────────────────────────────────
const FinalCTA = ({ navigate }) => (
  <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-testid="final-cta-section">
    {/* Background */}
    <div className="absolute inset-0 bg-gradient-to-b from-blue-600/10 via-blue-600/5 to-transparent pointer-events-none" />
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />

    <div className="max-w-3xl mx-auto text-center relative">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Comece agora e{' '}
          <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
            venda ainda hoje
          </span>
        </h2>
        <p className="text-base sm:text-lg text-zinc-400 mb-10 max-w-xl mx-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Milhares de vendedores já estão usando a Needi para automatizar suas vendas. Falta só você.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/register')}
            className="h-14 px-10 text-base sm:text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-[0_0_25px_rgba(37,99,235,0.5)] hover:-translate-y-0.5 transition-all"
            data-testid="final-cta-register"
          >
            <Rocket className="w-5 h-5 mr-2" />
            Criar conta grátis
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate('/access')}
            className="h-14 px-8 text-base bg-white/10 hover:bg-white/20 text-white rounded-full border border-white/20"
            data-testid="final-cta-access"
          >
            <Store className="w-5 h-5 mr-2" />
            Acessar uma loja
          </Button>
        </div>
      </motion.div>
    </div>
  </section>
);

// ─── Footer ───────────────────────────────────────────────────
const Footer = () => (
  <footer className="border-t border-white/5 py-16 px-4 sm:px-6 lg:px-8" data-testid="footer">
    <div className="max-w-7xl mx-auto text-center">
      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <span className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Needi</span>
      </div>
      <p className="text-sm text-zinc-600" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        © {new Date().getFullYear()} Needi. Todos os direitos reservados.
      </p>
    </div>
  </footer>
);

// ─── Main Component ───────────────────────────────────────────
export const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role === 'customer') {
      navigate('/access');
    }
  }, [user, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 200);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader className="w-10 h-10 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <Navbar />
      <HeroSection navigate={navigate} />
      <BenefitsSection />
      <DemoSection />
      <DifferentialsSection />
      <PricingSection navigate={navigate} />
      <FinalCTA navigate={navigate} />
      <Footer />
    </div>
  );
};
